import { create } from 'zustand';
import { ServiceCategory } from '../types/service';
import { City } from '../types/profile';
import {
  AlphabeticalSortOption,
  AllianceCountSortOption,
  PublishedAtSortOption,
} from '../types/search';

type SearchMode = 'freelancer' | 'project';

interface FreelancerSearchState {
  keyword: string;
  selectedLanguages: string[];
  selectedServices: ServiceCategory[];
  selectedCities: City[];
  alphabeticalSort: AlphabeticalSortOption;
  allianceCountSort: AllianceCountSortOption;
  rating: number;
  proximity: number;
}

interface ProjectSearchState {
  keyword: string;
  selectedLanguages: string[];
  selectedServices: ServiceCategory[];
  selectedCities: City[];
  alphabeticalSort: AlphabeticalSortOption | null;
  publishedAtSort: PublishedAtSortOption | null;
}

interface SearchStateStoreState {
  freelancer: FreelancerSearchState;
  project: ProjectSearchState;

  setFreelancerKeyword: (keyword: string) => void;
  setFreelancerLanguages: (languages: string[]) => void;
  setFreelancerServices: (services: ServiceCategory[]) => void;
  setFreelancerCities: (cities: City[]) => void;
  setFreelancerAlphabeticalSort: (sort: AlphabeticalSortOption) => void;
  setFreelancerAllianceCountSort: (sort: AllianceCountSortOption) => void;
  setFreelancerRating: (rating: number) => void;
  setFreelancerProximity: (proximity: number) => void;
  resetFreelancerFilters: () => void;

  setProjectKeyword: (keyword: string) => void;
  setProjectLanguages: (languages: string[]) => void;
  setProjectServices: (services: ServiceCategory[]) => void;
  setProjectCities: (cities: City[]) => void;
  setProjectAlphabeticalSort: (sort: AlphabeticalSortOption | null) => void;
  setProjectPublishedAtSort: (sort: PublishedAtSortOption | null) => void;
  resetProjectFilters: () => void;

  getKeyword: (mode: SearchMode) => string;
  getLanguages: (mode: SearchMode) => string[];
  getServices: (mode: SearchMode) => ServiceCategory[];
  getCities: (mode: SearchMode) => City[];
}

const initialFreelancerState: FreelancerSearchState = {
  keyword: '',
  selectedLanguages: [],
  selectedServices: [],
  selectedCities: [],
  alphabeticalSort: 'az',
  allianceCountSort: 'allianceDesc',
  rating: 0,
  proximity: 0,
};

const initialProjectState: ProjectSearchState = {
  keyword: '',
  selectedLanguages: [],
  selectedServices: [],
  selectedCities: [],
  alphabeticalSort: 'az',
  publishedAtSort: 'publishedAtDesc',
};

export const useSearchStateStore = create<SearchStateStoreState>((set, get) => ({
  freelancer: initialFreelancerState,
  project: initialProjectState,

  setFreelancerKeyword: (keyword: string) =>
    set(state => ({
      freelancer: { ...state.freelancer, keyword },
    })),

  setFreelancerLanguages: (languages: string[]) =>
    set(state => ({
      freelancer: { ...state.freelancer, selectedLanguages: languages },
    })),

  setFreelancerServices: (services: ServiceCategory[]) =>
    set(state => ({
      freelancer: { ...state.freelancer, selectedServices: services },
    })),

  setFreelancerCities: (cities: City[]) =>
    set(state => ({
      freelancer: { ...state.freelancer, selectedCities: cities },
    })),

  setFreelancerAlphabeticalSort: (sort: AlphabeticalSortOption) =>
    set(state => ({
      freelancer: { ...state.freelancer, alphabeticalSort: sort },
    })),

  setFreelancerAllianceCountSort: (sort: AllianceCountSortOption) =>
    set(state => ({
      freelancer: { ...state.freelancer, allianceCountSort: sort },
    })),

  setFreelancerRating: (rating: number) =>
    set(state => ({
      freelancer: { ...state.freelancer, rating },
    })),

  setFreelancerProximity: (proximity: number) =>
    set(state => ({
      freelancer: { ...state.freelancer, proximity },
    })),

  resetFreelancerFilters: () =>
    set({
      freelancer: initialFreelancerState,
    }),

  setProjectKeyword: (keyword: string) =>
    set(state => ({
      project: { ...state.project, keyword },
    })),

  setProjectLanguages: (languages: string[]) =>
    set(state => ({
      project: { ...state.project, selectedLanguages: languages },
    })),

  setProjectServices: (services: ServiceCategory[]) =>
    set(state => ({
      project: { ...state.project, selectedServices: services },
    })),

  setProjectCities: (cities: City[]) =>
    set(state => ({
      project: { ...state.project, selectedCities: cities },
    })),

  setProjectAlphabeticalSort: (sort: AlphabeticalSortOption | null) =>
    set(state => ({
      project: { ...state.project, alphabeticalSort: sort },
    })),

  setProjectPublishedAtSort: (sort: PublishedAtSortOption | null) =>
    set(state => ({
      project: { ...state.project, publishedAtSort: sort },
    })),

  resetProjectFilters: () =>
    set({
      project: initialProjectState,
    }),

  getKeyword: (mode: SearchMode) => {
    const state = get();
    return mode === 'freelancer' ? state.freelancer.keyword : state.project.keyword;
  },

  getLanguages: (mode: SearchMode) => {
    const state = get();
    return mode === 'freelancer'
      ? state.freelancer.selectedLanguages
      : state.project.selectedLanguages;
  },

  getServices: (mode: SearchMode) => {
    const state = get();
    return mode === 'freelancer'
      ? state.freelancer.selectedServices
      : state.project.selectedServices;
  },

  getCities: (mode: SearchMode) => {
    const state = get();
    return mode === 'freelancer' ? state.freelancer.selectedCities : state.project.selectedCities;
  },
}));
